export const getBuilderModeFromLocation = () => {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return (
    params.get("builderMode") === "1" &&
    params.get("builderSource") === "filament"
  );
};

const getBuilderComponentId = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getSafeOrigin = (value) => {
  if (!value || typeof value !== "string") return null;
  try {
    return new URL(value).origin;
  } catch (e) {
    return null;
  }
};

export const createBuilderBridge = () => {
  if (typeof window === "undefined") return null;
  if (!getBuilderModeFromLocation()) return null;
  const params = new URLSearchParams(window.location.search);
  const allowedParentOrigin = getSafeOrigin(params.get("builderParentOrigin"));
  if (!allowedParentOrigin) return null;
  const initialSelectedId = getBuilderComponentId(params.get("selectedComponentId"));

  const state = {
    selectedId: initialSelectedId,
    selectedType: null,
    hoveredId: null,
    selectedEl: null,
    hoveredEl: null,
    pendingObserver: null,
    pendingTimer: null,
    markerObserver: null,
  };

  const postToParent = (type, payload = {}) => {
    if (!allowedParentOrigin) return;
    window.parent?.postMessage(
      {
        source: "estore-iframe",
        type,
        payload,
      },
      allowedParentOrigin,
    );
  };

  const getElementByComponentId = (componentId) => {
    if (!Number.isFinite(componentId)) return null;
    return document.querySelector(`[data-builder-id="${componentId}"]`);
  };

  const scrollToElement = (el) => {
    if (!el) return;
    el.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });
  };

  const stopPendingSelection = () => {
    state.pendingObserver?.disconnect?.();
    state.pendingObserver = null;
    if (state.pendingTimer) window.clearTimeout(state.pendingTimer);
    state.pendingTimer = null;
  };

  const scrollToComponentWithRetry = (componentId, componentType = null) => {
    stopPendingSelection();
    const el = getElementByComponentId(componentId);
    if (el) {
      scrollToElement(el);
      return;
    }

    if (!Number.isFinite(componentId)) return;

    state.pendingObserver = new MutationObserver(() => {
      const nextElement = getElementByComponentId(componentId);
      if (!nextElement) return;
      stopPendingSelection();
      applyMarkers();
      scrollToElement(nextElement);
    });
    state.pendingObserver.observe(document.documentElement, { childList: true, subtree: true });
    state.pendingTimer = window.setTimeout(() => {
      stopPendingSelection();
      postToParent("COMPONENT_NOT_FOUND", {
        componentId,
        componentType,
      });
    }, 5000);
  };

  const applyMarkers = () => {
    const nextSelectedEl = getElementByComponentId(state.selectedId);
    const nextHoveredEl = getElementByComponentId(state.hoveredId);

    if (state.selectedEl && state.selectedEl !== nextSelectedEl) {
      state.selectedEl.classList.remove("builder-selected");
    }
    if (state.hoveredEl && state.hoveredEl !== nextHoveredEl) {
      state.hoveredEl.classList.remove("builder-hovered");
    }

    if (nextSelectedEl) {
      nextSelectedEl.classList.add("builder-selected");
    }
    if (nextHoveredEl) {
      nextHoveredEl.classList.add("builder-hovered");
    }

    state.selectedEl = nextSelectedEl;
    state.hoveredEl = nextHoveredEl;
    return {
      selectedEl: nextSelectedEl,
    };
  };

  const onMessage = (event) => {
    if (event.origin !== allowedParentOrigin) return;
    if (event.source !== window.parent) return;
    const data = event?.data || {};
    if (data?.source !== "estore-builder") return;
    if (data?.type === "SELECT_COMPONENT") {
      state.selectedId = getBuilderComponentId(data?.payload?.componentId);
      state.selectedType = data?.payload?.componentType ?? null;
      state.hoveredId = null;
      const markerState = applyMarkers();
      if (markerState?.selectedEl) {
        scrollToElement(markerState.selectedEl);
      } else {
        scrollToComponentWithRetry(state.selectedId, state.selectedType);
      }
    }
    if (data?.type === "HOVER_COMPONENT") {
      state.hoveredId = getBuilderComponentId(data?.payload?.componentId);
      applyMarkers();
    }
    if (data?.type === "CLEAR_SELECTION") {
      stopPendingSelection();
      state.selectedId = null;
      state.hoveredId = null;
      applyMarkers();
    }
  };

  window.addEventListener("message", onMessage);
  applyMarkers();
  state.markerObserver = new MutationObserver(() => {
    const previousSelectedEl = state.selectedEl;
    const markerState = applyMarkers();
    if (markerState.selectedEl && markerState.selectedEl !== previousSelectedEl) {
      scrollToElement(markerState.selectedEl);
    }
  });
  state.markerObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
  postToParent("BUILDER_READY");
  if (Number.isFinite(state.selectedId)) {
    scrollToComponentWithRetry(state.selectedId, state.selectedType);
  }
  return {
    applyMarkers,
    dispose: () => {
      stopPendingSelection();
      state.markerObserver?.disconnect?.();
      state.markerObserver = null;
      window.removeEventListener("message", onMessage);
    },
  };
};

const inferBuilderSettingTarget = (clickedTarget, componentRoot) => {
  const element = clickedTarget?.closest?.("button, a, input, textarea, select, img, picture, video, h1, h2, h3, h4, h5, h6, p, [role='button'], [class]") || clickedTarget;
  if (!element || !componentRoot?.contains?.(element)) {
    return { settingHint: "", elementLabel: "Komponen" };
  }

  const tag = String(element.tagName || "").toLowerCase();
  const componentType = componentRoot?.getAttribute?.("data-builder-type") || "";
  const className = typeof element.className === "string" ? element.className : "";
  const context = [
    tag,
    className,
    element.id,
    element.getAttribute?.("role"),
    element.getAttribute?.("aria-label"),
    element.getAttribute?.("alt"),
    element.getAttribute?.("data-testid"),
  ].filter(Boolean).join(" ").toLowerCase();

  const match = (pattern) => pattern.test(context);
  if (match(/search|pencarian/)) return { settingHint: "pencarian", elementLabel: "Pencarian" };
  if (match(/previous|next|arrow|slider|carousel|swiper|slick|pagination|dot/)) return { settingHint: "kontrol", elementLabel: "Kontrol slider" };
  if (match(/logo|brand-mark/)) {
    if (componentType === "brand") return { settingHint: "kartu", elementLabel: "Kartu merek" };
    return { settingHint: "logo", elementLabel: "Logo" };
  }
  if (match(/badge|tag|label|discount|promo|preorder/)) return { settingHint: "badge", elementLabel: "Badge" };
  if (match(/price|harga|amount|currency/)) return { settingHint: "harga", elementLabel: "Harga" };
  if (tag === "img" || tag === "picture" || tag === "video" || match(/image|gambar|thumbnail|photo|media/)) {
    if (componentType === "brand" || componentType === "testimonial") {
      return { settingHint: "kartu", elementLabel: componentType === "brand" ? "Kartu merek" : "Kartu testimoni" };
    }
    return { settingHint: "gambar", elementLabel: "Gambar" };
  }
  if (match(/subtitle|subheading|caption|eyebrow/)) return { settingHint: "subtitle", elementLabel: "Subjudul" };
  if (/^h[1-6]$/.test(tag) || match(/title|heading|headline|judul/)) return { settingHint: "judul", elementLabel: "Judul" };
  if (componentType === "footer" && (tag === "a" || match(/link|menu|navigation/))) {
    return { settingHint: "link", elementLabel: "Tautan footer" };
  }
  if (tag === "button" || tag === "a" || element.getAttribute?.("role") === "button" || match(/button|btn/)) {
    return { settingHint: "tombol", elementLabel: "Tombol" };
  }
  if (match(/card|product-item|item-card|tile/)) return { settingHint: "kartu", elementLabel: "Kartu" };
  if (tag === "p" || match(/description|content|copy|text|deskripsi/)) return { settingHint: "teks", elementLabel: "Teks" };
  if (element === componentRoot || element.parentElement === componentRoot) return { settingHint: "latar", elementLabel: "Latar komponen" };
  return { settingHint: "", elementLabel: "Komponen" };
};

export const notifyBuilderComponentClicked = (componentId, clickedTarget = null) => {
  if (typeof window === "undefined" || !getBuilderModeFromLocation()) return;
  const params = new URLSearchParams(window.location.search);
  if (params.get("componentPicker") !== "1") return;
  const allowedParentOrigin = getSafeOrigin(params.get("builderParentOrigin"));
  if (!allowedParentOrigin) return;
  const componentRoot = clickedTarget?.closest?.("[data-builder-id]") || null;
  const settingTarget = inferBuilderSettingTarget(clickedTarget, componentRoot);
  window.parent?.postMessage(
    {
      source: "estore-iframe",
      type: "COMPONENT_CLICKED",
      payload: { componentId, ...settingTarget },
    },
    allowedParentOrigin,
  );
};

export const handleBuilderComponentClick = (event, componentId) => {
  if (typeof window === "undefined" || !getBuilderModeFromLocation()) return;
  const params = new URLSearchParams(window.location.search);
  if (params.get("componentPicker") !== "1") return;

  event?.preventDefault?.();
  event?.stopPropagation?.();
  notifyBuilderComponentClicked(componentId, event?.target ?? null);
};
